import { useContext, useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Container, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../routing/LangRouter";
import { connect } from "react-redux";

//import redux stores
import { resetState } from "../../store/actions";

//import general components
import BaseTabs from "../../components/common/base-tabs/base-tabs";
import BaseTitle from "../../components/common/base-title/base-title";
import Chatbot from "../../components/view/chatbot/chatbot";
import ArrowIcon from "../../components/common/arrow-icon/arrow-icon";
import Footer from "../../components/common/footer/footer";

//import images and icons
import itineraryLogo from "../../assets/imgs/Trip Plan logo.png";
import flightLogo from "../../assets/imgs/Flight.png";
import hotelLogo from "../../assets/imgs/Hotel.png";
import accommodationLogo from "../../assets/imgs/accommodation.png";
import trainLogo from "../../assets/imgs/Train.png";
import busLogo from "../../assets/imgs/Bus.png";
import tourImage from "../../assets/imgs/tour11.png";
import jobImage from "../../assets/imgs/job.png";
import studyImage from "../../assets/imgs/study.png";
import { ArrowLeft2, ArrowRight2 } from "iconsax-react";

import itineraryMotion from "../../assets/motions/itinerary.mp4";
import flightMotion from "../../assets/motions/flight.mp4";
import hotelMotion from "../../assets/motions/hotel.mp4";
import busMotion from "../../assets/motions/bus.mp4";

//import styles
import "./landing.scss";

function Landing({ dispatch }) {
    //general variables
    const location = useLocation();
    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);

    //cleare redux and sessionStorage
    useEffect(() => {
        if (location.pathname === `/${locale}`) {
            sessionStorage.clear();
        }
        dispatch(resetState());
    }, [location]);

    //handle tab change
    const [activeTab, setActiveTab] = useState({ flight: true, hotel: false, accommodation: false, itinerary: false, train: false, bus: false });
    useEffect(() => {
        setActiveTab({
            flight: location.pathname === `/${locale}/flight-search/domestic` || location.pathname === `/${locale}/flight-search/international`,
            hotel: location.pathname === `/${locale}/hotel-search/domestic` || location.pathname === `/${locale}/hotel-search/international`,
            itinerary: location.pathname === `/${locale}/itinerary`,
            accommodation: location.pathname === `/${locale}/accommodation-search`,
            train: location.pathname === `/${locale}/train`,
            bus: location.pathname === `/${locale}/bus-search`,
        });
    }, [location.pathname]);

    const handleTabChange = (type, to) => {
        setActiveTab({ [type]: true });
    };

    //render customize title
    const renderTitle = (title, img, to) => {
        return (
            <Link to={to} className="landing__link">
                <Grid container item xs={12} direction="column" justifyContent="center" alignItems="center" rowGap={1}>
                    <img className="landing__tab-logo" src={img} />
                    <span>{title.split(",")[0]}</span>
                </Grid>
            </Link>
        );
    };

    //list of tabs
    const tabList = [
        { type: "flight", title: renderTitle(t("flight"), flightLogo, "flight-search/domestic"), activeTab: activeTab?.flight, status: "active" },
        { type: "hotel", title: renderTitle(t("hotel"), hotelLogo, "hotel-search/domestic"), activeTab: activeTab?.hotel, status: "active" },
        { type: "itinerary", title: renderTitle(t("itinerary"), itineraryLogo, "itinerary"), activeTab: activeTab?.itinerary, status: "active" },
        {
            type: "accommodation",
            title: renderTitle(t("accommodation"), accommodationLogo, "accommodation-search"),
            activeTab: activeTab?.accommodation,
            status: "active",
        },
        { type: "bus", title: renderTitle(`${t("bus")}`, busLogo, "bus-search"), activeTab: activeTab?.bus, status: "active" },
        { type: "train", title: renderTitle(`${t("train")}`, trainLogo), activeTab: activeTab?.train, status: "inactive" },
    ];

    //list of other services
    const otherServices = [
        { id: 1, title: t("tour"), img: tourImage, to: `/${locale}/tour` },
        { id: 2, title: t("Study Abroad.Study Abroad"), img: studyImage, to: `/${locale}/study-abroad` },
        { id: 3, title: t("General.Work Immigration"), img: jobImage, to: `/${locale}/work-immigration` },
        // { title: t("medicalTourism"), img: medicalTourImage, to: `/${locale}/medical-tourism` },
    ];

    // const [isLoginOpen, setIsLoginOpen] = useState(false);
    // useEffect(() => {
    //     if (searchParams.get("action") === "login") setIsLoginOpen(true);
    //     else setIsLoginOpen(false);
    // }, [searchParams]);

    return (
        <Grid container item xs={12} mb={5} rowGap={{ md: 4 }}>
            <Chatbot />
            <Grid className="landing__motion" container item xs={12}>
                <video
                    autoPlay
                    loop={activeTab.itinerary ? true : false}
                    muted
                    controls={false}
                    playsInline
                    webkit-playsinline="true"
                    src={
                        activeTab.itinerary
                            ? itineraryMotion
                            : activeTab.flight
                              ? flightMotion
                              : activeTab.hotel || activeTab.accommodation
                                ? hotelMotion
                                : activeTab.bus
                                  ? busMotion
                                  : ""
                    }
                />
            </Grid>

            <Container maxWidth="xl">
                <Grid container item xs={12} rowGap={2} justifyContent="center">
                    <Grid container item xs={12} md={8}>
                        <BaseTabs handleTabChange={handleTabChange} list={tabList} style={{ position: "static" }} />
                    </Grid>

                    <Outlet />

                    <Grid container item xs={12} rowGap={2} mb={15} mt={3}>
                        <BaseTitle
                            title={
                                <Grid container item xs={12} columnGap={1} justifyContent="space-between" alignItems="center">
                                    {t("otherServices")}
                                </Grid>
                            }
                            dividerColor="1px solid var(--light-color)"
                        />

                        <div className="landing__other-services">
                            {otherServices.map(service => {
                                return (
                                    <div className="landing__service" key={service?.id}>
                                        <Link to={service.to} className="landing__link">
                                            <img className="landing__tab-logo" src={service.img} />
                                            <span>{service.title}</span>
                                            <ArrowIcon
                                                icons={[ArrowLeft2, ArrowRight2]}
                                                isExpanded={locale === "en"}
                                                size="16"
                                                color="var(--teal-color-18)"
                                            />
                                        </Link>
                                    </div>
                                );
                            })}
                        </div>
                    </Grid>
                </Grid>
            </Container>
            <Footer />

            {/* <LoginModal open={isLoginOpen} setOpen={setIsLoginOpen} /> */}
        </Grid>
    );
}

export default connect()(Landing);
